import React from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import Grid from '@material-ui/core/Grid';
import {useStore} from 'effector-react';
import i18next from 'i18next';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {fetchParticipantList, fetchStorages} from 'api';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {AsyncSelect} from 'components/ui-kit/Select';

import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {profile$} from 'models/auth';
import {movingForm$, movingFormChanged, stepChanged} from 'models/documents/create';
import {onSetEditParticipant} from 'models/profile/common';
import {MovingFormData} from 'types';

import {FormContainer, FormField, FormWrapper, NavigationButtonsContainer} from '../common/styled';

import {StorageOption} from './helpers/Option';

const schema = yup.object().shape({
    storageLocationFromId: yup
        .string()
        .notOneOf(
            [yup.ref('storageLocationToId')],
            i18next.t('components.form.locationsMustDiffer'),
        )
        .required(i18next.t('components.form.required')),
    storageLocationToId: yup
        .string()
        .notOneOf(
            [yup.ref('storageLocationFromId')],
            i18next.t('components.form.locationsMustDiffer'),
        )
        .required(i18next.t('components.form.required')),
    note: yup
        .string()
        .trim()
        .nullable(),
});

export const DocumentForm = () => {
    const {t} = useTranslation();
    const movingForm = useStore(movingForm$);
    const profile = useStore(profile$);
    const tin = profile$.map((p) => p?.legalPerson?.tin).getState();

    const {control, handleSubmit, formState, trigger, getValues, setValue, watch} = useForm<
        MovingFormData
    >({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {...movingForm},
    });
    const storageLocationFrom = watch('storageLocationFrom');
    const storageLocationTo = watch('storageLocationTo');
    const onHandleChanged = (data: MovingFormData) => {
        stepChanged(1);
        movingFormChanged({
            ...data,
            ...(profile?.fullName && {employeeFullName: profile.fullName}),
        });
    };

    const NoStorageLocationSlot = () => {
        const [pending, setPending] = React.useState(false);
        const onRedirect = async () => {
            try {
                setPending(true);
                const participants = await fetchParticipantList();
                const user = participants.find((p) => p.id === profile?.client?.id);
                onSetEditParticipant(user || null);
                history.push(ROUTE.PROFILE_CREATE);
            } catch (error) {
                console.error(error);
            } finally {
                setPending(false);
            }
        };
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px',
                }}
            >
                Storage location is required
                <br />
                <Button
                    type="button"
                    variant={ButtonVariant.Outline}
                    onClick={onRedirect}
                    isLoading={pending}
                >
                    Go to profile and add it
                </Button>
            </div>
        );
    };

    const loadOptions = async () => {
        if (!tin) return;
        return (await fetchStorages()).storageLocations.map((value) => ({
            value: value.id,
            label: value.storageName,
            meta: {name: value.storageName ?? ''},
        }));
    };
    return (
        <FormContainer>
            <form onSubmit={handleSubmit(onHandleChanged)}>
                <FormWrapper>
                    <Grid container direction="column" spacing={3}>
                        <Grid item xs={12} md={4}>
                            <FormField>
                                <Input
                                    label={t('pages.documents.form.employeeName')}
                                    value={profile?.fullName}
                                    disabled
                                />
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormField>
                                <Controller
                                    name="storageLocationFromId"
                                    control={control}
                                    render={({value, onChange}, {invalid}) => (
                                        <AsyncSelect
                                            label={t('pages.documents.form.storageLocationFrom')}
                                            isClearable
                                            defaultOptions
                                            optionComponent={StorageOption}
                                            noOptionsMessage={() => <NoStorageLocationSlot />}
                                            value={
                                                value
                                                    ? {
                                                          value,
                                                          label: storageLocationFrom,
                                                      }
                                                    : null
                                            }
                                            onChange={(
                                                val: {value: string; meta?: {name: string}} | null,
                                            ) => {
                                                onChange(val ? val.value : null);
                                                setValue(
                                                    'storageLocationFrom',
                                                    val?.meta?.name ?? '',
                                                );
                                                if (getValues('storageLocationToId'))
                                                    trigger('storageLocationToId');
                                            }}
                                            loadOptions={loadOptions}
                                            hasError={invalid}
                                        />
                                    )}
                                />
                                {formState.errors?.storageLocationFromId && (
                                    <span style={{fontSize: 12, color: 'red'}}>
                                        {t(
                                            formState.errors?.storageLocationFromId
                                                ?.message as string,
                                        )}
                                    </span>
                                )}
                                <div style={{display: 'none'}}>
                                    <Controller
                                        defaultValue={''}
                                        control={control}
                                        name="storageLocationFrom"
                                        render={(props) => <input value={props.value} readOnly />}
                                    />
                                </div>
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormField>
                                <Controller
                                    name="storageLocationToId"
                                    control={control}
                                    render={({value, onChange}, {invalid}) => (
                                        <AsyncSelect
                                            label={t('pages.documents.form.storageLocationTo')}
                                            isClearable
                                            defaultOptions
                                            optionComponent={StorageOption}
                                            noOptionsMessage={() => <NoStorageLocationSlot />}
                                            value={
                                                value
                                                    ? {
                                                          value,
                                                          label: storageLocationTo,
                                                      }
                                                    : null
                                            }
                                            onChange={(
                                                val: {value: string; meta?: {name: string}} | null,
                                            ) => {
                                                onChange(val ? val.value : null);
                                                setValue(
                                                    'storageLocationTo',
                                                    val?.meta?.name ?? '',
                                                );
                                                if (getValues('storageLocationFromId')) {
                                                    trigger('storageLocationFromId');
                                                }
                                            }}
                                            loadOptions={loadOptions}
                                            hasError={invalid}
                                        />
                                    )}
                                />
                                {formState.errors?.storageLocationToId && (
                                    <span style={{fontSize: 12, color: 'red'}}>
                                        {t(
                                            formState.errors?.storageLocationToId
                                                ?.message as string,
                                        )}
                                    </span>
                                )}
                                <div style={{display: 'none'}}>
                                    <Controller
                                        defaultValue={''}
                                        control={control}
                                        name="storageLocationTo"
                                        render={(props) => <input value={props.value} readOnly />}
                                    />
                                </div>
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="note"
                                    render={({value, onChange}) => (
                                        <Input
                                            label={t('pages.documents.form.note')}
                                            value={value || ''}
                                            placeholder={t('pages.documents.form.note')}
                                            onChange={onChange}
                                            hasError={!!formState.errors.note}
                                        />
                                    )}
                                />
                                {formState.errors.note && (
                                    <span style={{fontSize: 12, color: 'red'}}>
                                        {t(formState.errors.note.message as string)}
                                    </span>
                                )}
                            </FormField>
                        </Grid>
                    </Grid>
                </FormWrapper>
                <NavigationButtonsContainer>
                    <span />
                    <Button
                        style={{width: 'auto'}}
                        variant={ButtonVariant.Normal}
                        type="submit"
                        disabled={!formState.isValid}
                    >
                        {t('components.form.nextStep')}
                    </Button>
                </NavigationButtonsContainer>
            </form>
        </FormContainer>
    );
};
